// client/src/coms/accounts/SignedInDevices.js
// Settings → Account: where this account is signed in, with per-device sign-out (revoked on the server).
import React, { useCallback, useEffect, useState } from 'react';
import { announceAccountChange, listSessions, restartApp, revokeSession } from './accountsApi';
import './accounts.css';

function when(ms) {
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return '';
  }
}

export default function SignedInDevices() {
  const [sessions, setSessions] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(0);

  const load = useCallback(async () => {
    try {
      const res = await listSessions();
      setSessions(res.sessions || []);
      setError('');
    } catch (e) {
      setSessions([]);
      setError(e.status === 401 ? 'Sign in to see your devices.' : 'Could not load your devices.');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const revoke = async (s) => {
    setBusy(s.id);
    try {
      const res = await revokeSession(s.id);
      if (res.signed_out) {
        announceAccountChange(null);
        restartApp('/comms');
        return;
      }
      await load();
    } catch (e) {
      setError(e.message || 'Could not sign that device out.');
    } finally {
      setBusy(0);
    }
  };

  return (
    <div className="coms-settings-row" data-testid="signed-in-devices">
      <div className="coms-settings-label">Signed-in devices</div>
      <div className="coms-settings-value">
        <div className="coms-panel-sub" style={{ opacity: 0.85 }}>
          Devices where this account is saved for quick switching. Signing a device out ends its session there straight away.
        </div>
        {error ? (
          <div className="vx-acct-error" role="alert">
            {error}
          </div>
        ) : null}
        <div className="vx-devices">
          {sessions === null ? <div className="coms-panel-sub">Loading…</div> : null}
          {(sessions || []).map((s) => (
            <div key={s.id} className="vx-device-row" data-testid="device-row">
              <div>
                <div style={{ fontWeight: 700 }}>
                  {s.device}
                  {s.this_device ? ' · this device' : ''}
                </div>
                <div className="vx-acct-sub">
                  Last used {when(s.last_used_at_ms)}
                  {s.expired ? ' · sign-in expired' : ` · expires ${when(s.expires_at_ms)}`}
                </div>
              </div>
              <button type="button" className="vx-btn small danger" disabled={busy === s.id} onClick={() => revoke(s)}>
                {s.this_device ? 'Sign out here' : 'Sign out'}
              </button>
            </div>
          ))}
          {sessions && !sessions.length && !error ? <div className="coms-panel-sub">No saved devices.</div> : null}
        </div>
      </div>
    </div>
  );
}
